import React, {useLayoutEffect, useMemo, useRef, useState} from "react";
import {Box, GlobalStyles} from "@mui/material";
import {EditorContent, useEditor} from "@tiptap/react";
import {Mark} from "@tiptap/core";
import {PrintoutOrientation} from "crqlar-react-common/dist/models/printout/printout.model";
import {
    printVariableChipSx,
    selectedVariableChipSx,
    unresolvedVariableChipSx,
    variableChipSx,
} from "../../common/variables/variableChipStyles";
import {
    parsePrintoutContent,
    type PrintoutDocumentSource,
    resolvePrintoutDocument as resolveStoredPrintoutDocument,
} from "../core/printoutDocument";
import {getPrintoutFontSx} from "../core/printoutFonts";
import {
    getPrintoutCssPageMargin,
    getPrintoutCssPageSize,
    getPrintoutOverallWidth,
    getPrintoutPaginationOptions,
    getPrintoutPageSize,
    PRINTOUT_PAGE_GAP_BORDER_COLOR,
} from "../core/printoutPageGeometry";
import {printoutDocumentTypographySx} from "../core/printoutTypography";
import {usePrintoutFontStylesheet} from "../core/usePrintoutFontStylesheet";
import {PrintoutPagination} from "../pagination/PrintoutPagination";
import {
    RESOLVED_PRINTOUT_VARIABLE_MARK,
    resolvePrintoutContent,
} from "./resolvePrintoutContent";
import {
    PrintoutVariableResolveContext,
    withPrintoutLanguageContext,
} from "../variables/printoutVariableResolver";
import {
    createPrintoutBaseExtensions,
    previewPrintoutBlockChromeSx,
    sharedPrintoutBlockBodySx,
} from "../blocks";
import {
    PRINTOUT_FOOTER_HEIGHT_VALUE,
    PRINTOUT_HEADER_HEIGHT_VALUE,
} from "../blocks/headerFooter/regionBlockStyles";
import {PRINTOUT_REGION_DECORATION_BREAK_VARIABLE} from "../blocks/headerFooter/printoutRegionOptions";

interface Props {
    context: PrintoutVariableResolveContext;
    language?: string;
    printout: PrintoutDocumentSource;
}

interface ResolvedRendererProps extends Props {
    content: string;
    resolvedLanguage: string;
}
const ResolvedPrintoutVariableMark = Mark.create({
    name: RESOLVED_PRINTOUT_VARIABLE_MARK,

    addAttributes() {
        return {
            key: {
                default: null,
            },
            nowrap: {
                default: false,
            },
        };
    },

    renderHTML({mark}) {
        const nowrap = mark.attrs.nowrap === true;
        const className = [
            "printout-resolved-variable",
            nowrap ? "printout-resolved-variable--nowrap" : "",
        ].filter(Boolean).join(" ");

        return [
            "span",
            {
                class: className,
                "data-printout-variable-key": mark.attrs.key || undefined,
            },
            0,
        ];
    },
});

const getPreviewExtensions = (orientation?: PrintoutOrientation) => [
    ...createPrintoutBaseExtensions({editable: false}),
    ResolvedPrintoutVariableMark,
    PrintoutPagination.configure(getPrintoutPaginationOptions(orientation)),
];

const ResolvedPrintoutDocumentRenderer = ({
    content,
    context,
    printout,
    resolvedLanguage,
}: ResolvedRendererProps) => {
    usePrintoutFontStylesheet(printout.fontUrl);

    const frameRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [previewSize, setPreviewSize] = useState<{
        height?: number;
        ready: boolean;
        scale: number;
    }>({ready: false, scale: 1});
    const editorExtensions = useMemo(
        () => getPreviewExtensions(printout.orientation),
        [printout.orientation],
    );
    const previewPageSize = useMemo(
        () => getPrintoutPageSize(printout.orientation),
        [printout.orientation],
    );
    const previewPageWidth = getPrintoutOverallWidth(previewPageSize) + 2;
    const previewPageHeight = previewPageSize.pageHeight + 2;
    const documentContext = useMemo(
        () => withPrintoutLanguageContext(context, resolvedLanguage),
        [context, resolvedLanguage],
    );
    const resolvedContent = useMemo(
        () => resolvePrintoutContent(parsePrintoutContent(content), documentContext),
        [content, documentContext],
    );

    const editor = useEditor({
        immediatelyRender: false,
        editable: false,
        extensions: editorExtensions,
        content: resolvedContent,
        editorProps: {
            attributes: {
                class: "printout-tiptap-preview",
            },
        },
    }, [editorExtensions, resolvedContent]);

    useLayoutEffect(() => {
        type PreviewMeasurement = {
            height: number;
            scale: number;
            sourceHeight: number;
            sourceWidth: number;
        };

        const readPreviewMeasurement = (): PreviewMeasurement | undefined => {
            const frame = frameRef.current;
            const content = contentRef.current;

            if (!frame || !content || !frame.clientWidth) return undefined;

            const page = content.querySelector<HTMLElement>(".printout-tiptap-preview") ?? content;
            const contentWidth = page.offsetWidth || page.scrollWidth;
            const contentHeight = page.offsetHeight || page.scrollHeight;

            if (!contentWidth || !contentHeight) return undefined;

            // Fit width only; multi-page previews remain legible and scroll vertically.
            const widthScale = frame.clientWidth / contentWidth;
            const nextScale = Math.min(1, widthScale);
            return {
                height: Math.ceil(contentHeight * nextScale),
                scale: nextScale,
                sourceHeight: contentHeight,
                sourceWidth: contentWidth,
            };
        };

        const measurementsMatch = (a?: PreviewMeasurement, b?: PreviewMeasurement) => (
            !!a
            && !!b
            && Math.abs(a.scale - b.scale) < 0.001
            && Math.abs(a.sourceHeight - b.sourceHeight) < 1
            && Math.abs(a.sourceWidth - b.sourceWidth) < 1
        );

        let animationFrame: number | undefined;
        let previousMeasurement: PreviewMeasurement | undefined;
        let stableFrameCount = 0;
        let previewReady = false;

        // Content, React node views, and pagination do not all settle in the same
        // frame. Keep the assembled page hidden until its measured geometry has
        // stayed unchanged across three animation frames.
        setPreviewSize(current => ({...current, ready: false}));

        const updatePreviewLayout = () => {
            animationFrame = undefined;
            const measurement = readPreviewMeasurement();

            if (!measurement) {
                animationFrame = window.requestAnimationFrame(updatePreviewLayout);
                return;
            }

            stableFrameCount = measurementsMatch(previousMeasurement, measurement)
                ? stableFrameCount + 1
                : 0;
            previousMeasurement = measurement;

            if (!previewReady && stableFrameCount >= 2) previewReady = true;

            setPreviewSize(current => {
                if (
                    current.ready === previewReady
                    && Math.abs(current.scale - measurement.scale) < 0.001
                    && Math.abs((current.height ?? 0) - measurement.height) < 1
                ) {
                    return current;
                }

                return {
                    height: measurement.height,
                    ready: previewReady,
                    scale: measurement.scale,
                };
            });

            if (!previewReady) {
                animationFrame = window.requestAnimationFrame(updatePreviewLayout);
            }
        };

        const schedulePreviewScaleUpdate = () => {
            if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(updatePreviewLayout);
        };

        schedulePreviewScaleUpdate();
        window.addEventListener("resize", schedulePreviewScaleUpdate);

        const observers: ResizeObserver[] = [];
        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(schedulePreviewScaleUpdate);
            if (frameRef.current) resizeObserver.observe(frameRef.current);
            if (contentRef.current) resizeObserver.observe(contentRef.current);
            observers.push(resizeObserver);
        }

        return () => {
            if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
            window.removeEventListener("resize", schedulePreviewScaleUpdate);
            observers.forEach(observer => observer.disconnect());
        };
    }, [editor, printout.fontFamily, printout.fontUrl, printout.orientation, resolvedContent]);

    return (
        <>
            {/* Screen pagination and printed pages share the geometry in core/printoutPageGeometry. */}
            <GlobalStyles
                styles={{
                    "@media print": {
                        "@page": {
                            margin: getPrintoutCssPageMargin(printout.orientation),
                            size: getPrintoutCssPageSize(printout.orientation),
                        },
                    },
                }}
            />

            <Box
                aria-busy={!previewSize.ready}
                className="printout-preview-frame"
                data-printout-preview-ready={previewSize.ready ? "true" : "false"}
                ref={frameRef}
                sx={{
                    overflow: "visible",
                    width: "100%",
                    "@media print": {
                        overflow: "visible",
                        width: "auto",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        height: previewSize.height ? `${previewSize.height}px` : "auto",
                        justifyContent: "center",
                        overflow: "visible",
                        position: "relative",
                        "@media print": {
                            display: "block",
                            height: "auto !important",
                        },
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            aspectRatio: `${previewPageWidth} / ${previewPageHeight}`,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: PRINTOUT_PAGE_GAP_BORDER_COLOR,
                            boxShadow: "0 18px 36px rgba(15, 23, 42, 0.14)",
                            boxSizing: "border-box",
                            left: "50%",
                            maxWidth: "100%",
                            opacity: previewSize.ready ? 0 : 1,
                            pointerEvents: "none",
                            position: "absolute",
                            top: 0,
                            transform: "translateX(-50%)",
                            transition: "opacity 120ms ease-out",
                            width: `${previewPageWidth}px`,
                            "@media print": {
                                display: "none",
                            },
                        }}
                    />

                    <Box
                        className="printout-preview-renderer"
                        ref={contentRef}
                        sx={{
                            display: "block",
                            flexShrink: 0,
                            opacity: previewSize.ready ? 1 : 0,
                            position: "relative",
                            transform: `scale(${previewSize.scale})`,
                            transformOrigin: "top center",
                            transition: previewSize.ready ? "opacity 120ms ease-out" : "none",
                            visibility: previewSize.ready ? "visible" : "hidden",
                            width: "max-content",
                            zIndex: 1,
                            "& .printout-tiptap-preview": {
                                ...printoutDocumentTypographySx,
                                bgcolor: "background.paper",
                                boxShadow: "0 18px 36px rgba(15, 23, 42, 0.14)",
                                boxSizing: "content-box",
                                flexShrink: 0,
                                ...getPrintoutFontSx(printout.fontFamily, resolvedContent.attrs?.headingFontFamily),
                                outline: "none",
                            },
                            "& .printout-tiptap-preview > *:last-child": {
                                mb: 0,
                            },
                            "& .printout-resolved-variable--nowrap": {
                                whiteSpace: "nowrap",
                            },
                            "& .printout-variable-node": {
                                display: "inline-flex",
                                lineHeight: "inherit",
                                position: "relative",
                                verticalAlign: "baseline",
                            },
                            "& .printout-variable-chip": {
                                ...variableChipSx,
                            },
                            [[
                                "& .printout-variable-chip.is-selected",
                                "& .printout-variable-node.is-selected .printout-variable-chip",
                                "& .ProseMirror-selectednode.printout-variable-chip",
                                "& .ProseMirror-selectednode.printout-variable-node .printout-variable-chip",
                            ].join(", ")]: {
                                ...selectedVariableChipSx,
                            },
                            "& .printout-variable-chip.is-unresolved": {
                                ...unresolvedVariableChipSx,
                            },
                            ...previewPrintoutBlockChromeSx,
                            ...sharedPrintoutBlockBodySx,
                            // Pagination markers are full-width floats. A data list wrapped in one
                            // atomic NodeView box is therefore moved below every marker when it grows
                            // beyond one page. Flatten only the read-only wrappers so its header and
                            // rows can flow between pages while the editor keeps one selectable block.
                            [[
                                "& .react-renderer.node-printoutDataList",
                                "& .react-renderer.node-printoutDataList > .printout-block-shell",
                                "& .react-renderer.node-printoutDataList .printout-block-content",
                                "& .react-renderer.node-printoutDataList .printout-data-list",
                                "& .react-renderer.node-printoutDataList .printout-data-list > *",
                            ].join(", ")]: {
                                display: "contents",
                            },
                            "& .react-renderer.node-printoutDataList .printout-data-list > * > :first-child": {
                                mt: "8mm",
                            },
                            "& .react-renderer.node-printoutDataList .printout-data-list > * > :last-child": {
                                mb: "8mm",
                            },
                            "& .printout-table-block-resize-handle": {
                                display: "none",
                            },
                            "& .printout-tiptap-preview, & .printout-tiptap-preview *": {
                                cursor: "default",
                            },
                            "@media print": {
                                ...sharedPrintoutBlockBodySx["@media print"],
                                m: 0,
                                minWidth: 0,
                                opacity: "1 !important",
                                p: 0,
                                transform: "none",
                                transformOrigin: "top left",
                                transition: "none",
                                visibility: "visible !important",
                                width: "auto",
                                "& .printout-tiptap-preview": {
                                    border: "0 !important",
                                    // Reserve measured repeating-region bands on every printed fragment.
                                    boxDecorationBreak: `var(${PRINTOUT_REGION_DECORATION_BREAK_VARIABLE}, slice)`,
                                    WebkitBoxDecorationBreak: `var(${PRINTOUT_REGION_DECORATION_BREAK_VARIABLE}, slice)`,
                                    boxShadow: "none",
                                    maxWidth: "none !important",
                                    minHeight: "auto !important",
                                    orphans: 1,
                                    paddingBottom: `${PRINTOUT_FOOTER_HEIGHT_VALUE} !important`,
                                    paddingLeft: "0 !important",
                                    paddingRight: "0 !important",
                                    paddingTop: `${PRINTOUT_HEADER_HEIGHT_VALUE} !important`,
                                    widows: 1,
                                    width: "auto !important",
                                },
                                "& .printout-tiptap-preview [data-crqlar-printout-pagination], & .printout-tiptap-preview .crqlar-printout-br-decoration": {
                                    display: "none !important",
                                },
                                "& .printout-variable-chip": {
                                    ...printVariableChipSx,
                                },
                                "& .printout-variable-chip.is-unresolved": {
                                    display: "none !important",
                                },
                                "& .printout-variable-node, & .printout-variable-chip": {
                                    breakInside: "avoid",
                                },
                                "& .printout-block-shell": {
                                    border: "0 !important",
                                    boxShadow: "none !important",
                                },
                                "& .printout-block-content": {
                                    backgroundColor: "transparent !important",
                                },
                                "& .printout-folio > * > *, & .printout-data-list > * > *": {
                                    breakInside: "avoid",
                                    pageBreakInside: "avoid",
                                },
                                "& .printout-folio-group-header": {
                                    breakAfter: "avoid",
                                    pageBreakAfter: "avoid",
                                },
                                "& .printout-folio > *, & .printout-data-list > *": {
                                    display: "block",
                                },
                            },
                        }}
                    >
                        <EditorContent editor={editor}/>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

const PrintoutDocumentRenderer = ({context, language, printout}: Props) => {
    const document = resolveStoredPrintoutDocument(printout, language ?? context.language);
    if (!document) return null;

    return (
        <ResolvedPrintoutDocumentRenderer
            content={document.content}
            context={context}
            language={language}
            printout={printout}
            resolvedLanguage={document.language}
        />
    );
};

export default PrintoutDocumentRenderer;
